import React from 'react';
import moment from 'moment';
import { Icon, Link, Tooltip } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import { formatDistance } from 'App/helpers/formatDistance';
import type Appointment from 'App/resources/Model/Appointment';
import AppointmentOutcome from 'App/resources/Model/AppointmentOutcome';
import './VisitCheckIn.scss';

type Props = {
	appointment: Pick<
		Appointment,
		| 'date'
		| 'outcome'
		| 'client'
		| 'checkInLatitude'
		| 'checkInLongitude'
		| 'checkInAccuracyM'
		| 'checkInCapturedAt'
		| 'checkInSource'
		| 'checkInDistanceM'
	>;
};

const googleMapsUrl = (latitude: number, longitude: number) =>
	`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`;

const VisitCheckIn = ({ appointment }: Props) => {
	const { t } = useTranslation();
	const {
		outcome,
		checkInLatitude: latitude,
		checkInLongitude: longitude,
		checkInAccuracyM: accuracyM,
		checkInCapturedAt: capturedAt,
		checkInSource: source,
		checkInDistanceM: distanceM
	} = appointment;

	if (outcome !== AppointmentOutcome.COMPLETED || latitude == null || longitude == null) {
		return null;
	}

	const company = appointment.client?.name;
	const coordinates = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
	const time = capturedAt
		? moment(capturedAt).format(moment(capturedAt).isSame(appointment.date, 'day') ? 'LT' : 'L LT')
		: null;

	const label =
		distanceM != null && company
			? time
				? t('appointment.checkIn.distanceTime', { distance: formatDistance(distanceM), company, time })
				: t('appointment.checkIn.distance', { distance: formatDistance(distanceM), company })
			: time
			? t('appointment.checkIn.pointTime', { time, coordinates })
			: t('appointment.checkIn.point', { coordinates });

	const hint = [
		t(source === 'app' ? 'appointment.checkIn.sourceApp' : 'appointment.checkIn.sourceBrowser'),
		accuracyM != null ? t('appointment.checkIn.accuracy', { accuracy: formatDistance(accuracyM, 1) }) : null
	]
		.filter(Boolean)
		.join(' · ');

	const classes = new BemClass('VisitCheckIn');

	return (
		<div className={classes.b()}>
			<Tooltip title={hint}>
				<Link
					href={googleMapsUrl(latitude, longitude)}
					target="_blank"
					rel="noreferrer"
					size="sm"
					color="grey-11"
				>
					<Icon name="map-marker" spacing="mr1" />
					{label}
				</Link>
			</Tooltip>
		</div>
	);
};

export default VisitCheckIn;
